import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { server } from "../../server";
import { useReactToPrint } from "react-to-print";
import useAuth from "../../useAuth";
import {
  FaPrint,
  FaFilePrescription,
  FaSearch,
  FaSave,
  FaCheckCircle,
  FaTimes,
  FaUserGraduate,
  FaGraduationCap,
  FaLayerGroup,
  FaHashtag,
  FaIdBadge,
  FaFilter,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import PrintableTC from "./PrintableTC";

export default function GenerateTransferCertificate() {
  const todayStr = new Date().toISOString().split("T")[0];

  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  // Search & Filter States (Class, Section, Roll No, Admission No)
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("ALL");
  const [selectedSection, setSelectedSection] = useState("ALL");
  const [rollNoInput, setRollNoInput] = useState("");
  const [admissionNoInput, setAdmissionNoInput] = useState("");

  // Form State
  const [formData, setFormData] = useState({
    applicationDate: todayStr,
    issueDate: todayStr,
    reasonForLeaving: "",
    conduct: "Good",
    remarks: "",
  });

  const [showPreview, setShowPreview] = useState(false);
  const [generatedTc, setGeneratedTc] = useState(null);

  // 1. Debounce Search Input (300ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchInput.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Fetch students list
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${server}/students`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStudents(res.data.data || []);
      } catch (error) {
        toast.error("Failed to load students");
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchStudents();
  }, [token]);

  // 2. Extract unique Classes & Sections for filters
  const classesList = useMemo(() => {
    const set = new Set();
    students.forEach((s) => {
      if (s.class) set.add(String(s.class));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [students]);

  const sectionsList = useMemo(() => {
    const set = new Set();
    students.forEach((s) => {
      if (s.section) set.add(String(s.section));
    });
    return Array.from(set).sort();
  }, [students]);

  const classCounts = useMemo(() => {
    const counts = {};
    students.forEach((s) => {
      if (s.class) {
        const clsKey = String(s.class);
        counts[clsKey] = (counts[clsKey] || 0) + 1;
      }
    });
    return counts;
  }, [students]);

  // 3. Filtered Students Logic
  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const matchSearch =
        !debouncedSearchTerm ||
        s.studentName?.toLowerCase().includes(debouncedSearchTerm) ||
        String(s.rollNumber || "").toLowerCase().includes(debouncedSearchTerm) ||
        String(s.admissionNumber || "").toLowerCase().includes(debouncedSearchTerm) ||
        s.fatherName?.toLowerCase().includes(debouncedSearchTerm) ||
        s.motherName?.toLowerCase().includes(debouncedSearchTerm);

      const matchClass =
        selectedClass === "ALL" || String(s.class || "") === selectedClass;

      const matchSection =
        selectedSection === "ALL" || String(s.section || "") === selectedSection;

      const matchRollNo =
        !rollNoInput.trim() ||
        String(s.rollNumber || "").toLowerCase().includes(rollNoInput.trim().toLowerCase());

      const matchAdmissionNo =
        !admissionNoInput.trim() ||
        String(s.admissionNumber || "").toLowerCase().includes(admissionNoInput.trim().toLowerCase());

      return matchSearch && matchClass && matchSection && matchRollNo && matchAdmissionNo;
    });
  }, [students, debouncedSearchTerm, selectedClass, selectedSection, rollNoInput, admissionNoInput]);

  const hasActiveFilters =
    searchInput !== "" ||
    selectedClass !== "ALL" ||
    selectedSection !== "ALL" ||
    rollNoInput !== "" ||
    admissionNoInput !== "";

  const handleClearFilters = () => {
    setSearchInput("");
    setSelectedClass("ALL");
    setSelectedSection("ALL");
    setRollNoInput("");
    setAdmissionNoInput("");
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePreview = (e) => {
    e.preventDefault();
    if (!selectedStudent) return toast.error("Please select a student!");
    if (formData.applicationDate > todayStr)
      return toast.error("Application Date cannot be in the future!");
    if (formData.issueDate > todayStr)
      return toast.error("Issue Date cannot be in the future!");
    if (!formData.reasonForLeaving)
      return toast.error("Please provide a reason for leaving!");
    setShowPreview(true);
  };

  const submitTC = async () => {
    if (!selectedStudent) return toast.error("Please select a student!");

    try {
      const payload = {
        studentId: selectedStudent.id,
        applicationDate: formData.applicationDate,
        issueDate: formData.issueDate,
        reasonForLeaving: formData.reasonForLeaving,
        conduct: formData.conduct,
        remarks: formData.remarks,
      };

      const res = await axios.post(
        `${server}/transferCertificates/register`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        toast.success("TC Generated Successfully!");
        const mappedTc = {
          ...res.data.data,
          student: selectedStudent,
        };
        setGeneratedTc(mappedTc);
        setShowPreview(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to generate TC");
    }
  };

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `TC_${selectedStudent?.studentName}_${generatedTc?.tcNumber || ""}`,
    pageStyle: `@page { size: A4 portrait; margin: 10mm; }`,
  });

  const previewTc = generatedTc || {
    student: selectedStudent,
    tcNumber: "DRAFT-XXXX",
    applicationDate: formData.applicationDate,
    issueDate: formData.issueDate,
    reasonForLeaving: formData.reasonForLeaving,
    conduct: formData.conduct,
    remarks: formData.remarks,
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Control Panel: Hidden during print */}
      <div className="print:hidden">
        <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-100 dark:bg-rose-950 text-rose-600 dark:text-rose-400 rounded-xl shadow-xs">
              <FaFilePrescription size={18} />
            </div>
            <div>
              <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">Issue Transfer Certificate</h1>
              <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Select a student, fill out clearing details, and generate their official Transfer Certificate</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/my/students/transfer-certificates")}
              className="bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs"
            >
              View TC List
            </button>
            {generatedTc && (
              <button
                onClick={handlePrint}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-1.5 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5"
              >
                <FaPrint size={11} /> Print Official TC
              </button>
            )}
          </div>
        </div>

        {!generatedTc && !showPreview ? (
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-800 p-6 grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            
            {/* SECTION 1: Select Student with Upgraded Filters */}
            <div className="lg:col-span-6 space-y-4">
              <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-800 pb-3">
                <h3 className="text-base font-black text-gray-900 dark:text-white flex items-center gap-2">
                  <span>1. Select Student</span>
                  <span className="text-[11px] font-bold bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 px-2.5 py-0.5 rounded-full">
                    {filteredStudents.length} / {students.length} Available
                  </span>
                </h3>

                {hasActiveFilters && (
                  <button
                    onClick={handleClearFilters}
                    className="text-[11px] font-extrabold text-rose-500 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 flex items-center gap-1 bg-rose-50 dark:bg-rose-950/40 px-2.5 py-1 rounded-xl transition"
                  >
                    <FaTimes size={11} /> Reset Filters
                  </button>
                )}
              </div>

              {/* Quick Class Selector Pills */}
              {classesList.length > 0 && (
                <div>
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                    <FaGraduationCap size={10} /> Quick Class Filter
                  </label>
                  <div className="flex items-center gap-1.5 overflow-x-auto pb-1.5 custom-scrollbar">
                    <button
                      type="button"
                      onClick={() => setSelectedClass("ALL")}
                      className={`px-3 py-1 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                        selectedClass === "ALL"
                          ? "bg-indigo-600 text-white shadow-xs"
                          : "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700"
                      }`}
                    >
                      All ({students.length})
                    </button>
                    {classesList.map((cls, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSelectedClass(cls)}
                        className={`px-3 py-1 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1 ${
                          selectedClass === cls
                            ? "bg-indigo-600 text-white shadow-xs"
                            : "bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-700"
                        }`}
                      >
                        Class {cls}
                        <span
                          className={`text-[10px] px-1.5 py-0.2 rounded-full ${
                            selectedClass === cls
                              ? "bg-white/20 text-white"
                              : "bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-300"
                          }`}
                        >
                          {classCounts[cls] || 0}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Upgraded Multi-Filter Grid: Class, Section, Roll No, Reg No */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-gray-50/80 dark:bg-slate-800/40 p-3 rounded-2xl border border-gray-200/80 dark:border-slate-800">
                {/* Class Dropdown */}
                <div>
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                    <FaGraduationCap size={10} /> Class
                  </label>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-500"
                  >
                    <option value="ALL">All Classes</option>
                    {classesList.map((c, idx) => (
                      <option key={idx} value={c}>
                        Class {c}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Section Dropdown */}
                <div>
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                    <FaLayerGroup size={10} /> Section
                  </label>
                  <select
                    value={selectedSection}
                    onChange={(e) => setSelectedSection(e.target.value)}
                    className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-500"
                  >
                    <option value="ALL">All Sections</option>
                    {sectionsList.map((sec, idx) => (
                      <option key={idx} value={sec}>
                        Section {sec}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Roll No Input */}
                <div>
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                    <FaHashtag size={10} /> Roll No
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="e.g. 12"
                      value={rollNoInput}
                      onChange={(e) => setRollNoInput(e.target.value)}
                      className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-500"
                    />
                    {rollNoInput && (
                      <button
                        type="button"
                        onClick={() => setRollNoInput("")}
                        className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 text-[10px]"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>

                {/* Admission No Input */}
                <div>
                  <label className="block text-[10px] font-extrabold uppercase text-gray-400 dark:text-slate-400 mb-1 flex items-center gap-1">
                    <FaIdBadge size={10} /> Reg No
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="e.g. 1024"
                      value={admissionNoInput}
                      onChange={(e) => setAdmissionNoInput(e.target.value)}
                      className="w-full px-2 py-1.5 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-700 dark:text-slate-200 outline-none focus:border-indigo-500"
                    />
                    {admissionNoInput && (
                      <button
                        type="button"
                        onClick={() => setAdmissionNoInput("")}
                        className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 text-[10px]"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Search Bar (Placed Below Dropdowns) */}
              <div>
                <label className="block text-[10px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400 mb-1">
                  Search Student Name or Details
                </label>
                <div className="relative">
                  <FaSearch className="absolute left-3.5 top-3.5 text-gray-400 dark:text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search Name, Admission No, Roll No, Father..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900 focus:border-indigo-500 outline-none transition-all font-medium text-gray-800 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-500 text-xs shadow-xs"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={() => setSearchInput("")}
                      className="absolute right-3.5 top-3 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>

              {/* Active Filter Chips Summary */}
              {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-1.5 p-2 bg-indigo-50/60 dark:bg-indigo-950/40 rounded-xl border border-indigo-100 dark:border-indigo-900/50 text-[11px]">
                  <span className="font-extrabold text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
                    <FaFilter size={9} /> Applied Filters:
                  </span>
                  {selectedClass !== "ALL" && (
                    <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                      Class: {selectedClass}
                      <button onClick={() => setSelectedClass("ALL")} className="hover:text-rose-500">
                        <FaTimes size={9} />
                      </button>
                    </span>
                  )}
                  {selectedSection !== "ALL" && (
                    <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                      Sec: {selectedSection}
                      <button onClick={() => setSelectedSection("ALL")} className="hover:text-rose-500">
                        <FaTimes size={9} />
                      </button>
                    </span>
                  )}
                  {rollNoInput && (
                    <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                      Roll: {rollNoInput}
                      <button onClick={() => setRollNoInput("")} className="hover:text-rose-500">
                        <FaTimes size={9} />
                      </button>
                    </span>
                  )}
                  {admissionNoInput && (
                    <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                      Reg: {admissionNoInput}
                      <button onClick={() => setAdmissionNoInput("")} className="hover:text-rose-500">
                        <FaTimes size={9} />
                      </button>
                    </span>
                  )}
                  {searchInput && (
                    <span className="inline-flex items-center gap-1 bg-white dark:bg-slate-800 text-indigo-700 dark:text-indigo-300 font-bold px-2 py-0.5 rounded-lg border border-indigo-200 dark:border-indigo-800 shadow-2xs">
                      "{searchInput}"
                      <button onClick={() => setSearchInput("")} className="hover:text-rose-500">
                        <FaTimes size={9} />
                      </button>
                    </span>
                  )}
                  <button
                    onClick={handleClearFilters}
                    className="font-extrabold text-rose-600 dark:text-rose-400 hover:underline ml-auto"
                  >
                    Clear All
                  </button>
                </div>
              )}

              {/* Filtered Students List */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-slate-400">
                    Select Student ({filteredStudents.length})
                  </label>
                  <span className="text-[11px] text-gray-400 dark:text-slate-500 font-medium">
                    Click card to pick student for TC
                  </span>
                </div>

                <div className="max-h-80 overflow-y-auto space-y-2 pr-1 custom-scrollbar border border-gray-100 dark:border-slate-800 p-2 rounded-2xl bg-gray-50/50 dark:bg-slate-800/30">
                  {filteredStudents.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 dark:text-slate-500 bg-white dark:bg-slate-800/60 rounded-xl border border-dashed border-gray-200 dark:border-slate-700">
                      <FaUserGraduate className="text-3xl mx-auto mb-2 opacity-40 text-gray-400 dark:text-slate-500" />
                      <p className="text-sm font-bold text-gray-600 dark:text-slate-300">No students match filters</p>
                      <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Try adjusting search, class, section, or roll number.</p>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={handleClearFilters}
                          className="mt-3 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-bold text-xs rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900 transition"
                        >
                          Reset Filters
                        </button>
                      )}
                    </div>
                  ) : (
                    filteredStudents.map((s) => {
                      const isSelected = selectedStudent?.id === s.id;
                      return (
                        <div
                          key={s.id}
                          onClick={() => {
                            setSelectedStudent(s);
                            setGeneratedTc(null);
                            setShowPreview(false);
                          }}
                          className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between group ${
                            isSelected
                              ? "bg-indigo-600 dark:bg-indigo-600 text-white border-indigo-600 shadow-md ring-2 ring-indigo-300 dark:ring-indigo-500/50"
                              : "bg-white dark:bg-slate-800/90 text-gray-800 dark:text-slate-100 border-gray-200 dark:border-slate-700/80 hover:border-indigo-300 dark:hover:border-indigo-500/50 hover:bg-indigo-50/40 dark:hover:bg-slate-700/60"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-10 h-10 rounded-xl font-bold flex items-center justify-center text-sm ${
                                isSelected
                                  ? "bg-white/20 text-white"
                                  : "bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-800/60"
                              }`}
                            >
                              {s.profilePictureUrl ? (
                                <img
                                  src={s.profilePictureUrl}
                                  alt={s.studentName}
                                  className="w-full h-full object-cover rounded-xl"
                                />
                              ) : (
                                (s.studentName || "S").charAt(0).toUpperCase()
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={`font-bold text-sm ${isSelected ? "text-white" : "text-gray-900 dark:text-white"}`}>
                                  {s.studentName}
                                </span>
                                {s.admissionNumber && (
                                  <span
                                    className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${
                                      isSelected
                                        ? "bg-white/20 text-white"
                                        : "bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300"
                                    }`}
                                  >
                                    Reg: {s.admissionNumber}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5 text-xs">
                                <span className={isSelected ? "text-indigo-100 font-semibold" : "text-gray-500 dark:text-slate-400 font-medium"}>
                                  Class: {s.class} {s.section}
                                </span>
                                {s.rollNumber && (
                                  <>
                                    <span className={isSelected ? "text-indigo-200" : "text-gray-300 dark:text-slate-600"}>•</span>
                                    <span className={isSelected ? "text-indigo-100 font-semibold" : "text-gray-600 dark:text-slate-300 font-bold"}>
                                      Roll: {s.rollNumber}
                                    </span>
                                  </>
                                )}
                                {s.fatherName && (
                                  <>
                                    <span className={isSelected ? "text-indigo-200" : "text-gray-300 dark:text-slate-600"}>•</span>
                                    <span className={isSelected ? "text-indigo-100" : "text-gray-500 dark:text-slate-400"}>
                                      Father: {s.fatherName}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {isSelected ? (
                              <FaCheckCircle className="text-white text-lg animate-pulse" />
                            ) : (
                              <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                Select →
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Selected Profile Summary Card */}
              {selectedStudent && (
                <div className="p-5 bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-slate-800 dark:to-slate-900 border border-indigo-200 dark:border-indigo-900/60 rounded-2xl shadow-sm relative">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white font-black flex items-center justify-center text-lg shadow-md">
                        {selectedStudent.profilePictureUrl ? (
                          <img
                            src={selectedStudent.profilePictureUrl}
                            alt={selectedStudent.studentName}
                            className="w-full h-full object-cover rounded-2xl"
                          />
                        ) : (
                          (selectedStudent.studentName || "S").charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <h4 className="font-extrabold text-indigo-950 dark:text-white text-base">
                          {selectedStudent.studentName}
                        </h4>
                        <span className="text-xs font-bold text-indigo-600 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/60 px-2 py-0.5 rounded-full">
                          Reg: {selectedStudent.admissionNumber || "N/A"}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedStudent(null)}
                      className="text-xs text-red-500 dark:text-red-400 font-bold hover:underline"
                    >
                      Deselect
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-y-2 text-xs border-t border-indigo-200/60 dark:border-slate-700/60 pt-3">
                    <span className="text-gray-500 dark:text-slate-400 font-medium">Class & Section:</span>
                    <span className="font-bold text-gray-800 dark:text-slate-200">
                      {selectedStudent.class} {selectedStudent.section}
                    </span>
                    <span className="text-gray-500 dark:text-slate-400 font-medium">Roll Number:</span>
                    <span className="font-bold text-gray-800 dark:text-slate-200">
                      {selectedStudent.rollNumber || "N/A"}
                    </span>
                    <span className="text-gray-500 dark:text-slate-400 font-medium">Father's Name:</span>
                    <span className="font-bold text-gray-800 dark:text-slate-200">
                      {selectedStudent.fatherName || "N/A"}
                    </span>
                    <span className="text-gray-500 dark:text-slate-400 font-medium">Mother's Name:</span>
                    <span className="font-bold text-gray-800 dark:text-slate-200">
                      {selectedStudent.motherName || "N/A"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* TC Details Form */}
            <form onSubmit={handlePreview} className="lg:col-span-6 space-y-6">
              <h3 className="text-lg font-bold text-gray-800 dark:text-white border-b border-gray-100 dark:border-slate-800 pb-4">
                2. TC Details
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Application Date
                  </label>
                  <input
                    type="date"
                    name="applicationDate"
                    required
                    max={todayStr}
                    value={formData.applicationDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all font-medium text-gray-700 dark:text-slate-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Issue Date
                  </label>
                  <input
                    type="date"
                    name="issueDate"
                    required
                    max={todayStr}
                    value={formData.issueDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all font-medium text-gray-700 dark:text-slate-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                  Reason For Leaving
                </label>
                <input
                  type="text"
                  name="reasonForLeaving"
                  required
                  placeholder="e.g. Higher Studies, Parent Transfer"
                  value={formData.reasonForLeaving}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all font-medium text-gray-700 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Character / Conduct
                  </label>
                  <select
                    name="conduct"
                    value={formData.conduct}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all appearance-none font-medium text-gray-700 dark:text-slate-200"
                  >
                    <option value="Excellent">Excellent</option>
                    <option value="Very Good">Very Good</option>
                    <option value="Good">Good</option>
                    <option value="Satisfactory">Satisfactory</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">
                    Remarks
                  </label>
                  <input
                    type="text"
                    name="remarks"
                    placeholder="Any extra remarks..."
                    value={formData.remarks}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all font-medium text-gray-700 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500"
                  />
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={!selectedStudent}
                  className={`w-full py-4 rounded-2xl flex items-center justify-center font-bold text-lg transition-all ${
                    !selectedStudent
                      ? "bg-gray-200 dark:bg-slate-800 text-gray-400 dark:text-slate-600 cursor-not-allowed"
                      : "bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-500 shadow-xl shadow-indigo-500/20"
                  }`}
                >
                  <FaSave className="mr-3" /> Preview Certificate
                </button>
              </div>
            </form>
          </div>
        ) : generatedTc ? (
          <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/60 rounded-3xl p-6 mb-12 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-4">
              <FaCheckCircle className="text-4xl text-emerald-500 dark:text-emerald-400" />
              <div>
                <h3 className="text-xl font-extrabold text-emerald-900 dark:text-emerald-200">
                  Certificate successfully generated!
                </h3>
                <p className="text-emerald-700 dark:text-emerald-400 font-medium text-sm">
                  TC No:{" "}
                  <span className="bg-emerald-200 dark:bg-emerald-900 px-2 py-0.5 rounded text-emerald-900 dark:text-emerald-100 mx-1">
                    {generatedTc?.tcNumber}
                  </span>{" "}
                  is ready for printing.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setGeneratedTc(null);
                setShowPreview(false);
              }}
              className="text-gray-500 dark:text-slate-400 font-bold hover:text-gray-800 dark:hover:text-white underline text-sm"
            >
              Generate another
            </button>
          </div>
        ) : null}
      </div>

      {/* Printable Area - Only mounts/shows properly if generatedTc exists */}
      {(showPreview || generatedTc) && (
        <div className="mt-12 print:mt-0">
          {showPreview && !generatedTc && (
            <div className="print:hidden flex flex-col md:flex-row justify-between items-center bg-yellow-50 dark:bg-amber-950/40 border border-yellow-200 dark:border-amber-900/60 p-6 rounded-3xl mb-8 shadow-sm">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-bold text-yellow-800 dark:text-amber-300">
                  Preview Mode
                </h3>
                <p className="text-yellow-700 dark:text-amber-400 text-sm mt-1">
                  Review the certificate layout below. Click Confirm to finalize
                  and generate the official TC Number.
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-6 py-2.5 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-200 font-bold rounded-2xl border border-gray-300 dark:border-slate-700 hover:bg-gray-100 dark:hover:bg-slate-700 transition shadow-sm"
                >
                  Back to Edit
                </button>
                <button
                  onClick={submitTC}
                  className="px-6 py-2.5 bg-indigo-600 dark:bg-indigo-600 text-white font-bold rounded-2xl hover:bg-indigo-700 dark:hover:bg-indigo-500 transition shadow-lg shadow-indigo-500/20 flex items-center gap-2"
                >
                  <FaCheckCircle /> Confirm & Generate
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-center mb-20 bg-gray-50 dark:bg-slate-950 print:bg-white overflow-hidden">
            <PrintableTC ref={componentRef} tc={previewTc} />
          </div>
        </div>
      )}
    </div>
  );
}
